/**
 * Companies Page
 *
 * Lista och hantera företag med DataTable
 */

import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Eye,
  Pencil,
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Building2,
  Sparkles,
} from "lucide-react";
import {
  companiesApi,
  subscriptionsApi,
  type Company,
  type BillingCompany,
} from "@/lib/api";
import { DataTable } from "@/components/ui";
import type { ColumnDef, RowAction } from "@/components/ui";
import { CreateCompanyModal } from "@/components/CreateCompanyModal";
import { EditCompanyModal } from "@/components/EditCompanyModal";
import { PageMeta, DASHBOARD_PAGE_META } from "@/lib/seo";
import { PaymentModal } from "@/components/PaymentModal";

const billingStatusConfig: Record<
  string,
  { label: string; style: string; icon: typeof CheckCircle }
> = {
  active_subscription: {
    label: "Aktiv",
    style: "bg-green-100 text-green-700",
    icon: CheckCircle,
  },
  canceled_subscription: {
    label: "Avslutas",
    style: "bg-yellow-100 text-yellow-700",
    icon: Clock,
  },
  past_due: {
    label: "Förfallen",
    style: "bg-red-100 text-red-700",
    icon: AlertTriangle,
  },
  inactive: {
    label: "Ej aktiverat",
    style: "bg-gray-100 text-gray-500",
    icon: XCircle,
  },
};

export function Companies() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [activatingCompany, setActivatingCompany] = useState<Company | null>(
    null
  );
  const [billingMap, setBillingMap] = useState<
    Record<string, BillingCompany["billingStatus"]>
  >({});
  const [searchFilter, setSearchFilter] = useState("");
  const [createdAtFilter, setCreatedAtFilter] = useState("");

  const loadCompanies = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await companiesApi.list();
      if (response.success && response.data) {
        setCompanies(response.data.companies);
      }
    } catch {
      toast.error("Kunde inte ladda företag");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadBilling = useCallback(async () => {
    try {
      const response = await subscriptionsApi.getBilling();
      if (response.success && response.data) {
        const map: Record<string, BillingCompany["billingStatus"]> = {};
        for (const c of response.data.companies) {
          map[c.companyId] = c.billingStatus;
        }
        setBillingMap(map);
      }
    } catch {
      // Non-critical — table still works without billing info
    }
  }, []);

  useEffect(() => {
    loadCompanies();
    loadBilling();
  }, [loadCompanies, loadBilling]);

  const handleCompanyCreated = useCallback(
    (company: Company) => {
      setCompanies((prev) => [company, ...prev]);
    },
    []
  );

  const handleCompanyUpdated = useCallback(
    (updated: Company) => {
      setCompanies((prev) =>
        prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c))
      );
    },
    []
  );

  // Client-side filtering
  const data = useMemo(() => {
    let result = companies;
    if (searchFilter) {
      const q = searchFilter.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.organizationNumber.includes(q)
      );
    }
if (createdAtFilter) {
      result = result.filter(
        (c) =>
          new Date(c.createdAt).toLocaleDateString("sv-SE") === createdAtFilter
      );
    }
    return result;
  }, [companies, searchFilter, createdAtFilter]);

  const columns: ColumnDef<Company>[] = useMemo(
    () => [
      {
        id: "name",
        header: "Företag",
        sortable: true,
        sortAccessor: (row) => row.name,
        filter: {
          type: "text" as const,
          value: searchFilter,
          onChange: setSearchFilter,
          placeholder: "Sök namn eller org.nr...",
        },
        cell: (row) => (
          <div>
            <p className="text-sm font-medium text-gray-900">{row.name}</p>
            <p className="text-xs text-gray-500">
              {row.organizationNumber}
            </p>
          </div>
        ),
      },
      {
        id: "createdAt",
        header: "Skapad",
        hideBelow: "md",
        sortable: true,
        sortAccessor: (row) => new Date(row.createdAt),
        filter: {
          type: "date" as const,
          value: createdAtFilter,
          onChange: setCreatedAtFilter,
        },
        cell: (row) => (
          <span className="text-sm text-gray-500">
            {new Date(row.createdAt).toLocaleDateString("sv-SE")}
          </span>
        ),
      },
      {
        id: "billingStatus",
        header: "Status",
        hideBelow: "sm",
        sortable: true,
        sortAccessor: (row) => billingMap[row.id] || "inactive",
        cell: (row) => {
          const status = billingMap[row.id] || "inactive";
          const config =
            billingStatusConfig[status] || billingStatusConfig.inactive;
          const Icon = config.icon;
          const isInactive = !status || status === "inactive";
          return (
            <span
              role={isInactive ? "button" : undefined}
              onClick={
                isInactive
                  ? (e) => {
                      e.stopPropagation();
                      setActivatingCompany(row);
                    }
                  : undefined
              }
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.style}${isInactive ? " cursor-pointer hover:bg-gray-200 transition-colors" : ""}`}
            >
              <Icon size={12} />
              {config.label}
            </span>
          );
        },
      },
    ],
    [searchFilter, createdAtFilter, billingMap]
  );

  const rowActions: RowAction<Company>[] = useMemo(
    () => [
      {
        key: "activate",
        label: "Aktivera",
        icon: CreditCard,
        onClick: (row) => setActivatingCompany(row),
        visible: (row) => {
          const status = billingMap[row.id];
          return !status || status === "inactive";
        },
      },
      {
        key: "edit",
        label: "Redigera",
        icon: Pencil,
        onClick: (row) => setEditingCompany(row),
      },
      {
        key: "view",
        label: "Visa",
        icon: Eye,
        onClick: (row) => navigate(`/dashboard/companies/${row.id}`),
      },
    ],
    [navigate, billingMap]
  );

  return (
    <>
      <PageMeta
        title={DASHBOARD_PAGE_META.companies.title}
        description={DASHBOARD_PAGE_META.companies.description}
        noIndex
      />
      <div className="space-y-8">
        {/* ═══════════════════════════════════════════════════════════════════
            PREMIUM HEADER SECTION
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-8 shadow-2xl">
          {/* Decorative elements */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMtOS45NDEgMC0xOCA4LjA1OS0xOCAxOHM4LjA1OSAxOCAxOCAxOCAxOC04LjA1OSAxOC0xOC04LjA1OS0xOC0xOC0xOHptMCAzMmMtNy43MzIgMC0xNC02LjI2OC0xNC0xNHM2LjI2OC0xNCAxNC0xNCAxNCA2LjI2OCAxNCAxNC02LjI2OCAxNC0xNCAxNHoiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjAyIi8+PC9nPjwvc3ZnPg==')] opacity-30" />
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />

          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            {/* Title section */}
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/10 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/10 shadow-lg">
                <Building2 className="text-white" size={28} />
              </div>
              <div>
                <div className="inline-flex items-center gap-2 mb-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/10">
                  <Sparkles className="text-amber-400" size={12} />
                  <span className="text-white/80 text-xs font-medium tracking-wide">
                    {companies.length} {companies.length === 1 ? "företag" : "företag"} registrerade
                  </span>
                </div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                  Mina företag
                </h1>
                <p className="text-slate-300 text-sm mt-1">
                  Hantera dina företag och deras årsredovisningar
                </p>
              </div>
            </div>

            {/* Add company button - blue/indigo gradient matching hero */}
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl blur-lg opacity-40 group-hover:opacity-60 transition-opacity" />
              <button
                onClick={() => setShowCreateModal(true)}
                className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 px-5 py-3 shadow-lg transition-all duration-300 hover:-translate-y-0.5"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
                <div className="relative flex items-center gap-3 text-white font-medium">
                  <Plus size={18} />
                  <span>Lägg till företag</span>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            COMPANIES TABLE
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm hover:shadow-md transition-shadow overflow-hidden">
          <DataTable
            columns={columns}
            data={data}
            rowKey={(row) => row.id}
            isLoading={isLoading}
            emptyMessage="Inga företag hittades"
            onRowClick={(row) => navigate(`/dashboard/companies/${row.id}`)}
            rowActions={rowActions}
          />
        </div>

      <CreateCompanyModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCompanyCreated={handleCompanyCreated}
      />

      {editingCompany && (
        <EditCompanyModal
          isOpen={!!editingCompany}
          onClose={() => setEditingCompany(null)}
          company={editingCompany}
          onUpdated={handleCompanyUpdated}
        />
      )}

      {activatingCompany && (
        <PaymentModal
          isOpen={!!activatingCompany}
          onClose={() => setActivatingCompany(null)}
          companyName={activatingCompany.name}
          companyId={activatingCompany.id}
          onActivated={() => {
            setActivatingCompany(null);
            loadBilling();
          }}
        />
      )}
    </div>
    </>
  );
}
